import React, { useState, useEffect } from 'react'
import { Box, Button, Card, CardContent, Grid, Typography, Chip, Tooltip } from '@mui/material'
import { useForm } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { format, subDays } from 'date-fns'
import FormProvider from '../../../common/hook-form/FormProvider'
import RHFTextField from '../../../common/hook-form/RHFTextField'
import RHFDateRangePickerField from '../../../common/hook-form/RHFDateRangePickerField'
import { MerchantFilterCached } from '../../../common/ProshipFormComponents'
import ProzoDataGrid from '../../../common/ProzoDataGrid'
import { apiConstants, post } from '../../../common/constants'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined'

const PAGE_SIZE = 20

const METHOD_COLOR = { GET: 'success', POST: 'primary', PUT: 'warning', DELETE: 'error', PATCH: 'warning' }

function parseApiCall(lastAPICalled) {
  if (!lastAPICalled) return { method: null, path: null }
  const idx = lastAPICalled.indexOf(' ')
  if (idx === -1) return { method: null, path: lastAPICalled }
  return {
    method: lastAPICalled.slice(0, idx).toUpperCase(),
    path: lastAPICalled.slice(idx + 1),
  }
}

function formatDateTime(val) {
  if (!val) return '—'
  try { return format(new Date(val), 'dd MMM yyyy, HH:mm:ss') } catch { return val }
}

export default function SecurityAuditTab() {
  const { enqueueSnackbar } = useSnackbar()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(PAGE_SIZE)
  const [filters, setFilters] = useState(null)

  const methods = useForm({
    defaultValues: {
      merchant: null,
      username: '',
      remoteIP: '',
      fromDate: subDays(new Date(), 7),
      toDate: new Date(),
    },
  })

  const { handleSubmit, reset } = methods

  const handleReset = () => {
    reset({ merchant: null, username: '', remoteIP: '', fromDate: subDays(new Date(), 7), toDate: new Date() })
    setRows([])
    setSubmitted(false)
    setPage(0)
    setFilters(null)
  }

  const onSubmit = (data) => {
    setPage(0)
    setFilters({
      merchantId: data.merchant || undefined,
      username: data.username?.trim() || undefined,
      remoteIP: data.remoteIP?.trim() || undefined,
      fromDate: data.fromDate ? format(new Date(data.fromDate), 'dd-MM-yyyy') : undefined,
      toDate: data.toDate ? format(new Date(data.toDate), 'dd-MM-yyyy') : undefined,
    })
  }

  useEffect(() => {
    if (!filters) return
    setLoading(true)
    post(apiConstants.AUDIT_API_SECURITY, { ...filters, offset: page * sizePerPage, limit: sizePerPage })
      .then((res) => {
        const raw = res?.data
        const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : []
        setRows(list.map((r, i) => ({
          ...r,
          id: i,
          httpMethod: parseApiCall(r.lastAPICalled).method,
          apiPath: parseApiCall(r.lastAPICalled).path,
        })))
        setSubmitted(true)
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || 'Failed to fetch API security audit'
        enqueueSnackbar(msg, { variant: 'error' })
      })
      .finally(() => setLoading(false))
  }, [filters, page, sizePerPage]) // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [
    {
      field: 'latestTimestamp',
      headerName: 'Time',
      flex: 1.5,
      renderCell: (p) => (
        <Typography variant="body2">{formatDateTime(p.value)}</Typography>
      ),
    },
    {
      field: 'username',
      headerName: 'Username',
      flex: 1.5,
      renderCell: (p) => <Typography variant="body2">{p.value || '—'}</Typography>,
    },
    {
      field: 'remoteIP',
      headerName: 'IP Address',
      flex: 1.2,
      renderCell: (p) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {p.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'httpMethod',
      headerName: 'Method',
      flex: 0.7,
      renderCell: (p) =>
        p.value ? (
          <Chip
            label={p.value}
            size="small"
            color={METHOD_COLOR[p.value] || 'default'}
          />
        ) : (
          <Typography variant="body2">—</Typography>
        ),
    },
    {
      field: 'apiPath',
      headerName: 'API Endpoint',
      flex: 2,
      renderCell: (p) => (
        <Tooltip title={p.value || ''}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {p.value || '—'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'lastAPITrackingId',
      headerName: 'Tracking ID',
      flex: 1.2,
      renderCell: (p) => (
        <Tooltip title={p.value || ''}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {p.value ? p.value.split('-')[0] + '…' : '—'}
          </Typography>
        </Tooltip>
      ),
    },
  ]

  return (
    <Box>
      <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: 'none' }}>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fafafa', borderBottom: '1px solid', borderColor: 'divider' }}>
          <FilterAltOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.primary' }}>Search Filters</Typography>
        </Box>
        <CardContent>
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <MerchantFilterCached fullWidth />
              </Grid>
              <Grid item xs={12} md={3}>
                <RHFTextField name="username" label="Username" placeholder="user@example.com" />
              </Grid>
              <Grid item xs={12} md={3}>
                <RHFTextField name="remoteIP" label="IP Address" placeholder="e.g. 103.45.67.89" />
              </Grid>
              <Grid item xs={12} md={3}>
                <RHFDateRangePickerField label="Date Range" />
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button type="submit" variant="contained" size="medium" disabled={loading} startIcon={<SearchOutlinedIcon />}>
                    Search
                  </Button>
                  <Button type="button" variant="outlined" size="medium" onClick={handleReset} startIcon={<RestartAltOutlinedIcon />}>
                    Reset
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </FormProvider>
        </CardContent>
      </Card>

      {submitted && (
        <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: 'none' }}>
          <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fafafa' }}>
            <Typography variant="subtitle2" fontWeight={600}>Results</Typography>
            <Chip label={`${rows.length} records`} size="small" color="primary" variant="outlined" />
          </Box>
          {rows.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <InboxOutlinedIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
              <Typography variant="subtitle1" color="text.secondary" fontWeight={500}>No records found</Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>Try adjusting your filters and search again</Typography>
            </Box>
          ) : (
            <CardContent sx={{ px: 0, pb: '0 !important' }}>
              <ProzoDataGrid
                rows={rows}
                columns={columns}
                rowCount={Number.MAX_VALUE}
                page={page}
                setPage={setPage}
                sizePerPage={sizePerPage}
                setSizePerPage={setSizePerPage}
                paginationMode="server"
                disableColumnMenu
                disableColumnFilter
              />
            </CardContent>
          )}
        </Card>
      )}
    </Box>
  )
}
